import { api, FilterOptionsResponse } from "../index";
import {
	IAnime,
	IComment,
	IGallery,
	INVITATION_RESPONSE_TYPE,
	Invitations,
	InvitationLastPaymentTransaction,
	InvitationStats,
	IWeddingInvitationComments,
	IWeddingInvitationComment,
	INVITATION_PRICING_PLANS_ENUM
} from "./dto/invitations.dto";

const WEDDING_INVITATION_ENDPOINT = "/wedding-invitations";
const WEDDING_INVITATION_ENDPOINT_MANAGEMENT = "/wedding-invitations/manage";
const USER_INVITATION_ENDPOINT = "/users/invitation";

export const InvitationsAPI = api.injectEndpoints({
	endpoints: build => ({
		getWeddingInvitations: build.query<{ invitations: Invitations[] }, { filter?: string }>({
			query: ({ filter }) => ({ url: `${WEDDING_INVITATION_ENDPOINT}`, params: { filter }, method: "GET" })
		}),
		validateWeddingInvitation: build.query<
			{ canAccess: boolean; isTemplate: boolean },
			{ id: string; password?: string }
		>({
			query: ({ id, password }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/validate/${id}`,
				params: { password },
				method: "GET"
			})
		}),
		getWeddingInvitation: build.query<{ invitation: Invitations }, { id: string; password?: string }>({
			query: ({ id, password }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${id}`,
				params: { password },
				method: "GET"
			})
		}),
		getInvitationLastPayment: build.query<
			{ planId: INVITATION_PRICING_PLANS_ENUM; oldPayedAmount: number },
			{ id: string }
		>({
			query: ({ id }) => ({
				url: `${USER_INVITATION_ENDPOINT}/${id}/lastPayment`,
				method: "GET"
			})
		}),
		getWeddingInvitationGallery: build.query<{ gallery: IGallery[] }, { id: string; password?: string }>({
			query: ({ id, password }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${id}/gallery`,
				params: { password },
				method: "GET"
			})
		}),
		getWeddingInvitationComment: build.query<{ comment: IComment }, IWeddingInvitationComment>({
			query: ({ id, password, anonymous_token }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${id}/comment`,
				params: { password, anonymous_token },
				method: "GET"
			}),
			providesTags: ["invitation_comment"]
		}),
		getWeddingInvitationComments: build.query<FilterOptionsResponse<IComment[]>, IWeddingInvitationComments>({
			query: ({ id, password, page, perPage }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${id}/comments`,
				params: { page, perPage, password },
				method: "GET"
			}),
			providesTags: ["invitation_comments"]
		}),
		updateComment: build.mutation<
			{ success: boolean },
			{
				_id: string;
				name: string;
				email: string;
				comment: string;
				response: INVITATION_RESPONSE_TYPE;
				anonymous_token: string;
				recaptchaToken: string;
				password?: string;
				emailOTP?: string;
			}
		>({
			query: ({ _id, name, email, comment, response, recaptchaToken, anonymous_token, password, emailOTP }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/comment/${anonymous_token}`,
				params: { password, emailOTP },
				method: "PUT",
				body: { name, email, comment, response, recaptchaToken }
			}),
			invalidatesTags: ["invitation_comments", "invitation_comment"]
		}),
		deleteComment: build.mutation<
			{ success: boolean },
			{
				_id: string;
				anonymous_token: string;
				recaptchaToken: string;
				password?: string;
			}
		>({
			query: ({ _id, recaptchaToken, anonymous_token, password }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/comment/${anonymous_token}`,
				params: { password },
				method: "DELETE",
				body: { recaptchaToken }
			}),
			invalidatesTags: ["invitation_comments", "invitation_comment"]
		}),
		addComment: build.mutation<
			{ success: boolean; response: unknown },
			{
				_id: string;
				name: string;
				email: string;
				comment: string;
				response: INVITATION_RESPONSE_TYPE;
				anonymous_token: string;
				recaptchaToken: string;
				password?: string;
				emailOTP?: string;
			}
		>({
			query: ({ _id, name, email, comment, response, recaptchaToken, anonymous_token, password, emailOTP }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/comment`,
				params: { password, emailOTP },
				method: "POST",
				body: { name, email, comment, response, recaptchaToken, anonymous_token }
			}),
			invalidatesTags: ["invitation_comments", "invitation_comment"]
		}),
		retrieveResponse: build.mutation<
			{ success: boolean; response?: { anonymous_token: string } },
			{
				_id: string;
				email: string;
				recaptchaToken: string;
				password?: string;
				emailOTP?: string;
			}
		>({
			query: ({ _id, email, recaptchaToken, password, emailOTP }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/comment/retrieve`,
				params: { password, emailOTP },
				method: "PUT",
				body: { email, recaptchaToken }
			}),
			invalidatesTags: []
		}),
		unDeleteResponse: build.mutation<
			{ success: boolean; response?: { anonymous_token: string } },
			{
				_id: string;
				email: string;
				recaptchaToken: string;
				password?: string;
				emailOTP?: string;
			}
		>({
			query: ({ _id, email, recaptchaToken, password, emailOTP }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/comment/unDelete`,
				params: { password, emailOTP },
				method: "PUT",
				body: { email, recaptchaToken }
			}),
			invalidatesTags: ["invitation_comments"]
		}),
		getWeddingInvitationStats: build.query<InvitationStats, { id: string; password?: string }>({
			query: ({ id, password }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/stats`,
				params: { password },
				method: "GET"
			})
		}),
		postView: build.mutation<
			{ success: boolean },
			{
				_id: string;
				deviceId: string;
				password: string;
			}
		>({
			query: ({ _id, deviceId, password }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/pageView`,
				params: { password },
				method: "POST",
				body: { deviceId }
			})
		}),
		updateInvitationMemory: build.mutation<
			{ success: boolean },
			{
				_id: string;
				gallery: IGallery[];
				user_email: string;
				password: string;
				anonymous_token: string;
				recaptchaToken: string;
			}
		>({
			query: ({ _id, password, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/memories`,
				params: { password },
				method: "PUT",
				body
			}),
			invalidatesTags: ["invitation_memories"]
		}),
		deleteInvitationMemory: build.mutation<
			{ success: boolean },
			{ _id: string; memoryItemId: string; anonymous_token: string; password: string; recaptchaToken: string }
		>({
			query: ({ _id, password, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${_id}/memory`,
				method: "DELETE",
				params: { password },
				body
			}),
			invalidatesTags: ["invitation_memories"]
		}),
		getWeddingInvitationMemories: build.query<
			FilterOptionsResponse<IGallery[]>,
			{ id: string; page?: number; perPage?: number; email?: string; password?: string; manage?: boolean }
		>({
			query: ({ id, email, password, page, perPage, manage }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT}/${id}/memories`,
				params: { email, password, page, perPage, manage },
				method: "GET"
			}),
			providesTags: ["invitation_memories"]
		}),
		////////////////////////////////////////////////////
		///////////// Management endpoints /////////////////
		////////////////////////////////////////////////////
		getManageWeddingInvitations: build.query<{ invitations: Invitations[] }, { id: string }>({
			query: ({ id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/list/${id}`,
				method: "GET"
			}),
			providesTags: ["Invitations"]
		}),
		getManageWeddingInvitation: build.query<{ invitation: Invitations }, { id: string }>({
			query: ({ id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}`,
				method: "GET"
			}),
			providesTags: ["managed_invitation"]
		}),
		getManageWeddingInvitationAnime: build.query<IAnime[], void>({
			query: () => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/anime`,
				method: "GET"
			})
		}),
		getManageWeddingInvitationGallery: build.query<{ gallery: IGallery[] }, { id: string }>({
			query: ({ id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/gallery`,
				method: "GET"
			}),
			providesTags: ["managed_invitation_gallery"]
		}),
		getManageWeddingInvitationComments: build.query<FilterOptionsResponse<IComment[]>, IWeddingInvitationComments>({
			query: ({ id, password, page, perPage, status }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/comments`,
				params: { page, perPage, password, status },
				method: "GET"
			}),
			providesTags: (result, error, arg) => [{ type: "invitation_comments", id: arg.id }]
		}),
		creatInvitationFromTemplate: build.mutation<
			{ success: boolean; invitation_id: string },
			{ template_id: string; timezone: string; template_color: number }
		>({
			query: body => ({ url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/create-from-template`, method: "POST", body }),
			invalidatesTags: ["Invitations"]
		}),
		updateInvitation: build.mutation<{ success: boolean }, Partial<Invitations>>({
			query: ({ _id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/update/${_id}`,
				method: "PUT",
				body
			}),
			invalidatesTags: ["managed_invitation", "Invitations"]
		}),
		updateInvitationHTML: build.mutation<{ success: boolean }, { _id: string; memories?: boolean }>({
			query: ({ _id, memories }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${_id}/share-html`,
				method: "POST",
				body: { memories }
			}),
			invalidatesTags: ["managed_invitation"]
		}),
		updateInvitationGallery: build.mutation<{ success: boolean }, Partial<Invitations>>({
			query: ({ _id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/update/gallery/${_id}`,
				method: "PUT",
				body
			}),
			invalidatesTags: ["managed_invitation_gallery"]
		}),
		deleteInvitationGallery: build.mutation<{ success: boolean }, { _id: string; galleryItemId: string }>({
			query: ({ _id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/delete/gallery/${_id}`,
				method: "DELETE",
				body
			}),
			invalidatesTags: ["managed_invitation_gallery"]
		}),
		requestPublishInvitation: build.mutation<{ success: boolean }, { _id: string; cancel?: boolean }>({
			query: ({ _id, cancel }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/request-publish/${_id}`,
				method: "PUT",
				body: { cancel }
			}),
			invalidatesTags: ["managed_invitation", "Invitations"]
		}),
		deleteResponse: build.mutation<
			{ success: boolean },
			{
				_id: string;
				comment_id: string;
			}
		>({
			query: ({ _id, comment_id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${_id}/comment/${comment_id}`,
				method: "DELETE"
			}),
			invalidatesTags: ["invitation_comments", "invitation_comment"]
		}),
		approveResponse: build.mutation<
			{ success: boolean },
			{
				_id: string;
				comment_id: string;
			}
		>({
			query: ({ _id, comment_id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${_id}/comment/${comment_id}/approve`,
				method: "PUT"
			}),
			invalidatesTags: ["invitation_comments", "invitation_comment"]
		}),
		deleteWeddingInvitation: build.mutation<
			{ success: boolean },
			{
				_id: string;
			}
		>({
			query: ({ _id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${_id}`,
				method: "DELETE"
			}),
			invalidatesTags: ["Invitations"]
		}),
		getManageWeddingInvitationMemories: build.query<
			FilterOptionsResponse<IGallery[]> & { fileName: string },
			{ id: string; page: number; perPage: number }
		>({
			query: ({ id, page, perPage }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/memories`,
				params: { page, perPage },
				method: "GET"
			}),
			providesTags: ["manage_invitation_memories"]
		}),
		downloadManageWeddingInvitationMemories: build.query<Blob, { id: string }>({
			query: ({ id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/memories/download`,
				method: "GET",
				responseHandler: response => response.blob()
			})
		}),
		deleteOwnerInvitationMemory: build.mutation<{ success: boolean }, { _id: string; memoryItemId: string }>({
			query: ({ _id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${_id}/memory`,
				method: "DELETE",
				body
			}),
			invalidatesTags: ["invitation_memories"]
		}),
		updateOwnerInvitationMemory: build.mutation<
			{ success: boolean },
			{ _id: string; memoryItemId: string; is_private: boolean }
		>({
			query: ({ _id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${_id}/memory`,
				method: "PUT",
				body
			}),
			invalidatesTags: ["invitation_memories"]
		}),
		getManageWeddingInvitationMemoriesEmails: build.query<{ emails: string[] }, { id: string }>({
			query: ({ id }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/memories/emails`,
				method: "GET"
			}),
			providesTags: []
		}),
		shareOwnerInvitationMemoryByEmail: build.mutation<{ success: boolean }, { _id: string; emails: string[] }>({
			query: ({ _id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${_id}/memories/email-share`,
				method: "POST",
				body
			}),
			invalidatesTags: []
		}),
		createOwnerMemoriesQRCode: build.query<{ url: string }, { id: string }>({
			query: ({ id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/memories/qr-code`,
				method: "POST",
				body
			}),
			providesTags: []
		}),
		createOwnerInvitationQRCode: build.query<{ url: string }, { id: string }>({
			query: ({ id, ...body }) => ({
				url: `${WEDDING_INVITATION_ENDPOINT_MANAGEMENT}/${id}/qr-code`,
				method: "POST",
				body
			}),
			providesTags: []
		})
	})
});

export const {
	useGetWeddingInvitationsQuery,
	useGetWeddingInvitationQuery,
	useLazyGetWeddingInvitationQuery,
	useLazyGetInvitationLastPaymentQuery,
	useLazyGetWeddingInvitationGalleryQuery,
	useLazyValidateWeddingInvitationQuery,
	useCreatInvitationFromTemplateMutation,
	useAddCommentMutation,
	useRetrieveResponseMutation,
	useUnDeleteResponseMutation,
	useUpdateCommentMutation,
	useDeleteCommentMutation,
	useLazyGetWeddingInvitationCommentsQuery,
	useLazyGetWeddingInvitationCommentQuery,
	usePostViewMutation,
	useUpdateInvitationMemoryMutation,
	useGetWeddingInvitationMemoriesQuery,
	useLazyGetWeddingInvitationMemoriesQuery,
	useDeleteInvitationMemoryMutation,
	////////////////////////////////////////////////////
	///////////// Management endpoints /////////////////
	////////////////////////////////////////////////////
	useUpdateInvitationMutation,
	useUpdateInvitationGalleryMutation,
	useDeleteInvitationGalleryMutation,
	useRequestPublishInvitationMutation,
	useLazyGetManageWeddingInvitationQuery,
	useLazyGetManageWeddingInvitationGalleryQuery,
	useGetManageWeddingInvitationAnimeQuery,
	useGetManageWeddingInvitationsQuery,
	useGetWeddingInvitationStatsQuery,
	useLazyGetManageWeddingInvitationCommentsQuery,
	useDeleteResponseMutation,
	useApproveResponseMutation,
	useDeleteWeddingInvitationMutation,
	useLazyGetManageWeddingInvitationMemoriesQuery,
	useDeleteOwnerInvitationMemoryMutation,
	useLazyDownloadManageWeddingInvitationMemoriesQuery,
	useUpdateOwnerInvitationMemoryMutation,
	useGetManageWeddingInvitationMemoriesEmailsQuery,
	useShareOwnerInvitationMemoryByEmailMutation,
	useLazyCreateOwnerMemoriesQRCodeQuery,
	useLazyCreateOwnerInvitationQRCodeQuery,
	useUpdateInvitationHTMLMutation
} = InvitationsAPI;
